import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import Logo from './Logo';
import { 
  Play, 
  Square, 
  Monitor, 
  Loader2, 
  Globe,
  MousePointer,
  Keyboard,
  CheckCircle,
  AlertCircle,
  X,
  RefreshCw,
  List,
  ChevronLeft,
  ChevronRight,
  Clock,
  Trash2,
  Save,
  Server,
  Link,
  Eye,
  FileCheck,
  GripVertical
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin || '';

/**
 * VNC-based Live Recorder Component
 * Provides native browser experience via VNC streaming using noVNC iframe
 */
const VNCLiveRecorder = ({ 
  testId, 
  initialUrl = '', 
  onClose, 
  onSave,
  existingSteps = [],
  isResuming = false,
  testName: initialTestName = ''
}) => {
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [websocketPort, setWebsocketPort] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vncStatus, setVncStatus] = useState('disconnected');
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [steps, setSteps] = useState([]);
  const [currentUrl, setCurrentUrl] = useState(initialUrl || '');
  const [startingUrl, setStartingUrl] = useState(initialUrl || '');
  const [recordingTime, setRecordingTime] = useState(0);
  
  // Convert FlowTest steps to VNC step format for display
  const convertExistingStepsToVNCFormat = (flowSteps) => {
    if (!flowSteps || flowSteps.length === 0) return [];
    
    return flowSteps.map(step => {
      const baseStep = {
        timestamp: new Date().toISOString(),
        selector: step.selector?.value || '',
        url: ''
      };
      
      switch (step.action) {
        case 'navigate':
          return { ...baseStep, type: 'navigation', url: step.value || step.selector?.value };
        case 'click':
          return { ...baseStep, type: 'click', text: step.description || '', tagName: 'BUTTON' };
        case 'type':
          return { ...baseStep, type: 'type', value: step.value || '', placeholder: step.description };
        case 'select':
          return { ...baseStep, type: 'select', value: step.value, text: step.description };
        case 'assert_text':
        case 'assert_visible':
        case 'assert_exists':
          return { 
            ...baseStep, 
            type: 'assert', 
            assertType: step.action.replace('assert_', ''),
            expected: step.expected_text || '',
            passed: true
          };
        default:
          return { ...baseStep, type: step.action, text: step.description };
      }
    }).filter(Boolean);
  };
  
  // Track if we've auto-started for resume
  const autoStartedRef = React.useRef(false);
  
  // Load existing steps and set URL when resuming
  React.useEffect(() => {
    if (initialUrl) {
      setStartingUrl(initialUrl);
      setCurrentUrl(initialUrl);
      setSelectedEnvironmentId('custom');
    }
    
    // Load existing steps when resuming
    if (isResuming && existingSteps && existingSteps.length > 0) {
      const convertedSteps = convertExistingStepsToVNCFormat(existingSteps);
      setSteps(convertedSteps);
    }
  }, [initialUrl, isResuming, existingSteps?.length]);
  
  // Environment state
  const [environments, setEnvironments] = useState([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState('custom');
  const [loadingEnvironments, setLoadingEnvironments] = useState(true);
  
  // UI state
  const [vncIframeUrl, setVncIframeUrl] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [assertionMode, setAssertionMode] = useState(null); // null, 'text', 'visible', 'exists', 'title'
  const [assertionPrompt, setAssertionPrompt] = useState(null); // For text assertion input
  const [draggedStepIndex, setDraggedStepIndex] = useState(null); // For drag and drop reordering
  const [showSaveModal, setShowSaveModal] = useState(false); // For save test name prompt
  const [testName, setTestName] = useState(''); // Test case name
  
  // Refs
  const vncContainerRef = useRef(null);
  const vncIframeRef = useRef(null);
  const websocketRef = useRef(null);
  const timerRef = useRef(null);
  
  // Cleanup tracking refs - to prevent double stops and track stable sessions
  const stableSessionIdRef = useRef(null);
  const stoppedSessionIdsRef = useRef(new Set());
  const sessionStableRef = useRef(false);
  
  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token');
  };
  
  // Fetch environments on mount
  useEffect(() => {
    const fetchEnvironments = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/flow-testing/environments`,
          { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
        );
        setEnvironments(response.data || []);
      } catch (err) {
        console.error('Failed to load environments:', err);
        setEnvironments([]);
      } finally {
        setLoadingEnvironments(false);
      }
    };
    
    fetchEnvironments();
  }, []);
  
  // Handle environment selection
  const handleEnvironmentChange = (value) => {
    setSelectedEnvironmentId(value);
    if (value === 'custom') {
      setStartingUrl('');
    } else {
      const env = environments.find(e => e.id === value);
      if (env && env.base_url) {
        setStartingUrl(env.base_url);
      }
    }
  };
  
  // Recording timer
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setRecordingTime(0);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);
  
  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Initialize noVNC connection via iframe
  // Uses FastAPI proxy for multi-user support (routes to correct websockify port per session)
  const initializeVNC = useCallback((wsPort, sessId) => {
    setVncStatus('connecting');
    
    // Get backend URL, fallback to current origin
    const backendUrlStr = BACKEND_URL || window.location.origin;
    if (!backendUrlStr) {
      console.error('[VNC] No backend URL available');
      setError('Backend URL not configured');
      return;
    }
    
    try {
      const backendUrl = new URL(backendUrlStr);
      const wsProto = backendUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = backendUrl.host;
      
      // Use new v2 proxy endpoint for multi-user support
      // This routes through FastAPI to the correct websockify port for this session
      const wsUrl = `${wsProto}//${wsHost}/api/vnc-recording/vnc-proxy-v2/${sessId}`;
      
      console.log('[VNC] Using FastAPI proxy v2 for multi-user support');
      console.log('[VNC] WebSocket URL:', wsUrl);
      
      const noVncUrl = `${backendUrlStr}/api/vnc-recording/novnc/vnc_proxy.html?wsurl=${encodeURIComponent(wsUrl)}`;
      
      console.log('[VNC] noVNC iframe URL:', noVncUrl);
      setVncIframeUrl(noVncUrl);
      
      // Verify session is active before marking as connected
      setTimeout(async () => {
        try {
          await axios.get(
            `${backendUrlStr}/api/vnc-recording/session/${sessId}`,
            { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
          );
          setVncStatus('connected');
          setIsConnected(true);
        } catch {
          console.warn('[VNC] Session verification failed, but continuing...');
          // Still set connected since VNC may work even if this endpoint fails
          setVncStatus('connected');
          setIsConnected(true);
        }
      }, 2000);
    } catch (err) {
      console.error('[VNC] Failed to construct URL:', err, 'backendUrl:', backendUrlStr);
      setError(`Invalid backend URL: ${backendUrlStr}`);
    }
    
  }, []);
  
  // Start VNC session
  const startSession = async () => {
    if (!startingUrl || !startingUrl.startsWith('http')) {
      setError('Please enter a valid URL starting with http:// or https://');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/vnc-recording/start`,
        {
          starting_url: startingUrl,
          test_id: testId
        },
        {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          timeout: 60000
        }
      );
      
      const { session_id, websocket_port } = response.data;
      
      setSessionId(session_id);
      setWebsocketPort(websocket_port);
      setCurrentUrl(startingUrl);
      setIsRecording(true);
      
      // Wait a bit for the session to be fully ready, then connect WebSocket
      setTimeout(() => {
        connectStepWebSocket(session_id);
      }, 500);
      
      setTimeout(() => {
        initializeVNC(websocket_port, session_id);
      }, 2000);
      
      setIsLoading(false);
      
    } catch (err) {
      console.error('Failed to start VNC session:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to start recording session');
      setIsLoading(false);
    }
  };
  
  // Connect WebSocket for step updates
  const connectStepWebSocket = (sid) => {
    // Build WebSocket URL safely
    const backendUrlStr = BACKEND_URL || window.location.origin;
    let wsUrl;
    try {
      const backendUrl = new URL(backendUrlStr);
      const wsProto = backendUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${wsProto}//${backendUrl.host}/api/vnc-recording/ws/${sid}`;
    } catch (err) {
      console.error('[VNC Steps] Failed to construct WebSocket URL:', err);
      return;
    }
    
    console.log('[VNC Steps] Connecting to WebSocket:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    websocketRef.current = ws;
    
    ws.onopen = () => {
      console.log('[VNC Steps] WebSocket connected');
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[VNC Steps] Message received:', data.type);
        
        switch (data.type) {
          case 'session_started':
            setCurrentUrl(data.current_url);
            // Load any existing steps
            if (data.existing_steps && data.existing_steps.length > 0) {
              setSteps(data.existing_steps);
            }
            break;
          case 'step_recorded':
            console.log('[VNC Steps] Step recorded:', data.step.type);
            setSteps(prev => [...prev, data.step]);
            break;
          case 'navigation':
            setCurrentUrl(data.url);
            break;
          case 'error':
            console.error('[VNC Steps] Error:', data.message);
            break;
          default:
            break;
        }
      } catch (e) {
        console.error('[VNC Steps] Parse error:', e);
      }
    };
    
    ws.onerror = (error) => {
      console.error('[VNC Steps] WebSocket error:', error);
    };
    
    ws.onclose = (event) => {
      console.log('[VNC Steps] WebSocket closed:', event.code, event.reason);
      // Attempt to reconnect after 2 seconds if still recording
      if (isRecording && sessionId) {
        setTimeout(() => {
          console.log('[VNC Steps] Attempting reconnect...');
          connectStepWebSocket(sessionId);
        }, 2000);
      }
    };
  };
  
  // Disconnect VNC
  const disconnectVNC = () => {
    setVncIframeUrl(null);
    setVncStatus('disconnected');
    setIsConnected(false);
  };
  
  // Stop session
  const stopSession = async () => {
    if (!sessionId) return;
    
    // Mark session as stopped to prevent double-stop from cleanup
    const sid = sessionId;
    stoppedSessionIdsRef.current.add(sid);
    stableSessionIdRef.current = null;
    sessionStableRef.current = false;
    
    setIsLoading(true);
    
    try {
      disconnectVNC();
      
      await axios.post(
        `${BACKEND_URL}/api/vnc-recording/stop/${sid}`,
        {},
        { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
      );
      
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      
      setIsRecording(false);
      setSessionId(null);
      setWebsocketPort(null);
      setVncStatus('disconnected');
      
      if (onSave && steps.length > 0) {
        onSave(steps);
      }
      
    } catch (err) {
      console.error('Failed to stop session:', err);
      setError(err.response?.data?.detail || 'Failed to stop session');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Reconnect VNC
  const reconnectVNC = () => {
    if (sessionId && websocketPort) {
      disconnectVNC();
      setTimeout(() => {
        initializeVNC(websocketPort, sessionId);
      }, 1000);
    }
  };
  
  // Clear steps
  const clearSteps = () => {
    setSteps([]);
  };
  
  // Open save modal
  const openSaveModal = () => {
    setTestName(initialTestName || '');
    setShowSaveModal(true);
  };
  
  // Save test with user-provided name
  const saveTestWithName = () => {
    if (!testName.trim()) {
      setError('Please enter a test name');
      return;
    }
    
    if (onSave && steps.length > 0) {
      onSave(steps, testName.trim(), startingUrl);
    }
    setShowSaveModal(false);
    setTestName('');
  };
  
  // Delete a specific step
  const deleteStep = (index) => {
    setSteps(prev => prev.filter((_, i) => i !== index));
  };
  
  // Drag and drop handlers for reordering steps
  const handleDragStart = (e, index) => {
    setDraggedStepIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };
  
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedStepIndex(null);
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedStepIndex === null || draggedStepIndex === dropIndex) return;
    
    setSteps(prev => {
      const newSteps = [...prev];
      const [draggedStep] = newSteps.splice(draggedStepIndex, 1);
      newSteps.splice(dropIndex, 0, draggedStep);
      return newSteps;
    });
    setDraggedStepIndex(null);
  };
  
  // Start assertion mode
  const startAssertionMode = (type) => {
    setAssertionMode(type);
    if (type === 'title') {
      // Title assertion doesn't need element selection
      handleTitleAssertion();
    }
  };
  
  // Cancel assertion mode
  const cancelAssertionMode = () => {
    setAssertionMode(null);
    setAssertionPrompt(null);
  };
  
  // Handle click on VNC overlay for assertion
  const handleAssertionClick = async (e) => {
    if (!assertionMode || !sessionId) return;
    
    const overlay = e.currentTarget;
    const rect = overlay.getBoundingClientRect();
    
    // Calculate click position relative to the VNC view
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 1920);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 1080);
    
    console.log(`[Assertion] Click at (${x}, ${y}) for ${assertionMode}`);
    
    try {
      // Get element info at position from backend
      const response = await axios.post(
        `${BACKEND_URL}/api/vnc-recording/inspect-element/${sessionId}`,
        { x, y, assertion_type: assertionMode },
        { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
      );
      
      const elementInfo = response.data;
      console.log('[Assertion] Element info:', elementInfo);
      
      if (assertionMode === 'text') {
        // Show prompt for expected text
        setAssertionPrompt({
          elementInfo,
          defaultValue: elementInfo.text || ''
        });
      } else {
        // Create assertion step directly (exists/visible)
        const step = {
          type: 'assert',
          timestamp: new Date().toISOString(),
          assertType: assertionMode,
          selector: elementInfo.selector,
          selectors: elementInfo.selectors || [], // Include fallback selectors
          tagName: elementInfo.tagName,
          text: elementInfo.text,
          passed: true,
          url: currentUrl
        };
        
        setSteps(prev => [...prev, step]);
        setAssertionMode(null);
      }
    } catch (err) {
      console.error('[Assertion] Failed to inspect element:', err);
      setError('Failed to inspect element. Try clicking on a different element.');
      setTimeout(() => setError(null), 3000);
    }
  };
  
  // Handle title assertion
  const handleTitleAssertion = async () => {
    if (!sessionId) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/vnc-recording/page-info/${sessionId}`,
        { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
      );
      
      const pageInfo = response.data;
      
      setAssertionPrompt({
        elementInfo: { selector: 'title', text: pageInfo.title },
        defaultValue: pageInfo.title,
        isTitle: true
      });
    } catch (err) {
      console.error('[Assertion] Failed to get page info:', err);
      setError('Failed to get page title.');
      setAssertionMode(null);
    }
  };
  
  // Confirm text/title assertion with expected value
  const confirmTextAssertion = (expectedText) => {
    const step = {
      type: 'assert',
      timestamp: new Date().toISOString(),
      assertType: assertionPrompt.isTitle ? 'title' : 'text',
      selector: assertionPrompt.elementInfo.selector,
      selectors: assertionPrompt.elementInfo.selectors || [], // Include fallback selectors
      expected: expectedText,
      actual: assertionPrompt.elementInfo.text,
      passed: assertionPrompt.elementInfo.text?.includes(expectedText),
      url: currentUrl
    };
    
    setSteps(prev => [...prev, step]);
    setAssertionMode(null);
    setAssertionPrompt(null);
  };
  
  // Lock body scroll when modal is open
  useEffect(() => {
    // Save original styles
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    const originalHeight = document.body.style.height;
    
    // Lock body
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    return () => {
      // Restore original styles
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
      document.body.style.height = originalHeight;
    };
  }, []);
  
  // Mark session as stable after it's been running for a few seconds
  // Captures the specific sessionId that becomes stable
  useEffect(() => {
    if (sessionId && isRecording) {
      sessionStableRef.current = false;
      stableSessionIdRef.current = null;
      
      const sid = sessionId;
      const timeout = setTimeout(() => {
        sessionStableRef.current = true;
        stableSessionIdRef.current = sid;
        console.log('[VNC Cleanup] Session marked as stable', sid);
      }, 5000);
      
      return () => clearTimeout(timeout);
    } else {
      sessionStableRef.current = false;
      stableSessionIdRef.current = null;
    }
  }, [sessionId, isRecording]);
  
  // Cleanup on unmount only (empty dependency array)
  useEffect(() => {
    return () => {
      disconnectVNC();
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      
      // Stop only the specific stable session, and only if not already stopped
      const sidToStop = stableSessionIdRef.current;
      if (sidToStop && sessionStableRef.current && !stoppedSessionIdsRef.current.has(sidToStop)) {
        stoppedSessionIdsRef.current.add(sidToStop);
        console.log('[VNC Cleanup] Stopping stable session on unmount', sidToStop);
        axios.post(
          `${BACKEND_URL}/api/vnc-recording/stop/${sidToStop}`,
          {},
          { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
        ).catch(console.error);
      } else {
        console.log('[VNC Cleanup] Skipping stop on unmount', { 
          sidToStop, 
          stable: sessionStableRef.current,
          alreadyStopped: sidToStop ? stoppedSessionIdsRef.current.has(sidToStop) : false
        });
      }
    };
  }, []); // Empty dependency - only runs on unmount
  
  // Auto-start session when resuming (after all functions are defined)
  useEffect(() => {
    // Auto-start conditions: resuming + has URL + not already started + not loading + no active session + environments loaded
    if (isResuming && initialUrl && !autoStartedRef.current && !sessionId && !loadingEnvironments) {
      autoStartedRef.current = true;
      
      // Directly start the session using initialUrl (don't wait for state update)
      const autoStartSession = async () => {
        const urlToUse = initialUrl;
        if (!urlToUse || !urlToUse.startsWith('http')) {
          console.error('[VNCLiveRecorder] Invalid URL for auto-start:', urlToUse);
          autoStartedRef.current = false;
          return;
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
          console.log('[VNCLiveRecorder] Auto-start: calling backend with URL:', urlToUse);
          const response = await axios.post(
            `${BACKEND_URL}/api/vnc-recording/start`,
            {
              starting_url: urlToUse,
              test_id: testId
            },
            {
              headers: {
                'Authorization': `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json'
              },
              timeout: 60000
            }
          );
          
          const { session_id, websocket_port } = response.data;
          console.log('[VNCLiveRecorder] Auto-start: session created:', session_id);
          
          setSessionId(session_id);
          setWebsocketPort(websocket_port);
          setCurrentUrl(urlToUse);
          setStartingUrl(urlToUse);
          setIsRecording(true);
          
          // Wait a bit for the session to be fully ready, then connect WebSocket
          setTimeout(() => {
            connectStepWebSocket(session_id);
          }, 500);
          
          setTimeout(() => {
            initializeVNC(websocket_port, session_id);
          }, 2000);
          
          setIsLoading(false);
          
        } catch (err) {
          console.error('[VNCLiveRecorder] Auto-start failed:', err);
          setError(err.response?.data?.detail || err.message || 'Failed to start recording session');
          setIsLoading(false);
          autoStartedRef.current = false; // Allow retry
        }
      };
      
      // Small delay to ensure component is mounted and environments are loaded
      const timer = setTimeout(() => {
        autoStartSession();
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [isResuming, initialUrl, sessionId, loadingEnvironments, testId]);
  
  // Helper function to extract friendly field name from selector
  const getFriendlyFieldName = (selector, placeholder) => {
    if (!selector) return 'field';
    
    // Check for placeholder first (most user-friendly)
    if (placeholder) {
      return placeholder;
    }
    
    // Extract name from name attribute: input[name="first_name"] -> First Name
    const nameMatch = selector.match(/\[name=["']([^"']+)["']\]/);
    if (nameMatch) {
      return nameMatch[1]
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    
    // Extract from ID: #firstName -> First Name
    const idMatch = selector.match(/^#([a-zA-Z0-9_-]+)/);
    if (idMatch) {
      return idMatch[1]
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    
    // Extract from placeholder attribute: [placeholder="Enter name"] -> Enter name
    const placeholderMatch = selector.match(/\[placeholder=["']([^"']+)["']\]/);
    if (placeholderMatch) {
      return placeholderMatch[1];
    }
    
    // Extract from class if it looks like a field name
    const classMatch = selector.match(/\.([a-zA-Z]+[-_]?(?:input|field|text|name|email|password|phone|address))/i);
    if (classMatch) {
      return classMatch[1]
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    
    // Fallback: return a shortened selector
    if (selector.length > 30) {
      return selector.substring(0, 27) + '...';
    }
    return selector;
  };

  // Format step for display
  const formatStep = (step, index) => {
    let icon = <MousePointer className="h-4 w-4" />;
    let title = '';
    let description = '';
    let bgColor = 'bg-blue-50 border-blue-200';
    
    switch (step.type) {
      case 'click':
        icon = <MousePointer className="h-4 w-4 text-blue-500" />;
        title = 'Click';
        // Get friendly name for what was clicked
        if (step.text && step.text !== 'Focus on input' && !step.text.startsWith('Focus on')) {
          description = `on "${step.text}"`;
        } else if (step.tagName) {
          const fieldName = getFriendlyFieldName(step.selector, step.placeholder);
          description = `on ${fieldName}`;
        } else {
          description = step.selector ? `on ${getFriendlyFieldName(step.selector)}` : '';
        }
        bgColor = 'bg-blue-50 border-blue-200';
        break;
      case 'type':
        icon = <Keyboard className="h-4 w-4 text-green-500" />;
        title = 'Type';
        const fieldName = getFriendlyFieldName(step.selector, step.placeholder);
        description = `"${step.value}" into ${fieldName}`;
        bgColor = 'bg-green-50 border-green-200';
        break;
      case 'select':
        icon = <CheckCircle className="h-4 w-4 text-purple-500" />;
        title = 'Select';
        const selectFieldName = getFriendlyFieldName(step.selector);
        description = `"${step.text}" from ${selectFieldName}`;
        bgColor = 'bg-purple-50 border-purple-200';
        break;
      case 'submit':
        icon = <Play className="h-4 w-4 text-orange-500" />;
        title = 'Submit';
        description = step.buttonText || 'Form';
        bgColor = 'bg-orange-50 border-orange-200';
        break;
      case 'check':
        icon = <CheckCircle className="h-4 w-4 text-purple-500" />;
        title = step.checked ? 'Check' : 'Uncheck';
        description = getFriendlyFieldName(step.selector);
        bgColor = 'bg-purple-50 border-purple-200';
        break;
      case 'assert':
        const assertIcon = step.passed !== false ? 
          <CheckCircle className="h-4 w-4 text-emerald-500" /> : 
          <AlertCircle className="h-4 w-4 text-red-500" />;
        icon = assertIcon;
        bgColor = step.passed !== false ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200';
        
        switch (step.assertType) {
          case 'title':
            title = 'Assert Title';
            description = `contains "${step.expected}"`;
            break;
          case 'text':
            title = 'Assert Text';
            description = `"${step.expected}" in ${getFriendlyFieldName(step.selector)}`;
            break;
          case 'visible':
            title = 'Assert Visible';
            description = getFriendlyFieldName(step.selector);
            break;
          case 'exists':
            title = 'Assert Exists';
            description = getFriendlyFieldName(step.selector);
            break;
          default:
            title = 'Assert';
            description = step.assertType;
        }
        break;
      case 'navigation':
        icon = <Globe className="h-4 w-4 text-indigo-500" />;
        title = 'Navigate';
        try {
          const urlObj = step.url ? new URL(step.url) : null;
          description = urlObj ? `to ${urlObj.pathname}${urlObj.search || ''}` : `to ${step.url}`;
        } catch {
          description = `to ${step.url || 'page'}`;
        }
        bgColor = 'bg-indigo-50 border-indigo-200';
        break;
      case 'keypress':
        icon = <Keyboard className="h-4 w-4 text-amber-500" />;
        title = 'Press';
        description = `${step.key || 'Enter'} key`;
        bgColor = 'bg-amber-50 border-amber-200';
        break;
      default:
        title = step.type || 'Action';
        description = step.selector || '';
    }
    
    return (
      <div 
        key={index} 
        className={`flex items-start gap-2 p-3 rounded-lg border ${bgColor} transition-all hover:shadow-sm ${draggedStepIndex === index ? 'opacity-50' : ''} ${draggedStepIndex !== null && draggedStepIndex !== index ? 'border-dashed border-purple-300' : ''}`}
        draggable
        onDragStart={(e) => handleDragStart(e, index)}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => handleDragOver(e, index)}
        onDrop={(e) => handleDrop(e, index)}
      >
        {/* Drag Handle */}
        <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 pt-0.5">
          <GripVertical className="h-4 w-4" />
        </div>
        
        {/* Step Number */}
        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-white shadow-sm text-xs font-semibold text-gray-600 flex-shrink-0">
          {index + 1}
        </div>
        
        {/* Step Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-sm font-medium text-gray-800">{title}</span>
            <span className="text-sm text-gray-600 truncate" title={description}>{description}</span>
          </div>
        </div>
        
        {/* Delete Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteStep(index);
          }}
          className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-red-50 flex-shrink-0"
          title="Delete step"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  };
  
  // Get status color and text
  const getStatusInfo = () => {
    switch (vncStatus) {
      case 'connected': return { color: 'bg-green-500', text: 'Connected', pulse: false };
      case 'connecting': return { color: 'bg-yellow-500', text: 'Connecting...', pulse: true };
      case 'error': return { color: 'bg-red-500', text: 'Error', pulse: false };
      default: return { color: 'bg-gray-400', text: 'Disconnected', pulse: false };
    }
  };
  
  const statusInfo = getStatusInfo();
  
  const modalContent = (
    <div 
      className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex"
      style={{ 
        position: 'fixed',
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        zIndex: 2147483647,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0
      }}
    >
      {/* Left Sidebar */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-80'} bg-white flex flex-col transition-all duration-300 shadow-xl`}>
        {/* Logo Section */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed ? (
              <Logo size="md" showText={true} />
            ) : (
              <img 
                src="/assets/footer-icon.png" 
                alt="Lexa" 
                className="h-8 w-8 object-contain mx-auto"
              />
            )}
          </div>
        </div>
        
        {/* Recorded Steps Header */}
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2">
                <List className="h-5 w-5 text-purple-600" />
                <span className="font-semibold text-gray-800">Recorded Steps</span>
                {steps.length > 0 && (
                  <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">{steps.length}</Badge>
                )}
              </div>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        
        {/* Steps List */}
        {!sidebarCollapsed && (
          <>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {steps.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MousePointer className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-600">No steps recorded</p>
                  <p className="text-xs text-gray-400 mt-1 px-4">
                    Start interacting with the browser to capture your test steps
                  </p>
                </div>
              ) : (
                steps.map((step, index) => formatStep(step, index))
              )}
            </div>
            
            {/* Sidebar Footer Actions */}
            {isRecording && (
              <div className="p-3 border-t border-gray-100 bg-gradient-to-b from-gray-50 to-gray-100">
                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Assertions</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className={`justify-center text-xs ${assertionMode === 'text' ? 'bg-emerald-100 border-emerald-400' : ''}`}
                    onClick={() => startAssertionMode('text')}
                    disabled={!sessionId}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Text
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className={`justify-center text-xs ${assertionMode === 'visible' ? 'bg-emerald-100 border-emerald-400' : ''}`}
                    onClick={() => startAssertionMode('visible')}
                    disabled={!sessionId}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Visible
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className={`justify-center text-xs ${assertionMode === 'exists' ? 'bg-emerald-100 border-emerald-400' : ''}`}
                    onClick={() => startAssertionMode('exists')}
                    disabled={!sessionId}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Exists
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className={`justify-center text-xs ${assertionMode === 'title' ? 'bg-emerald-100 border-emerald-400' : ''}`}
                    onClick={() => startAssertionMode('title')}
                    disabled={!sessionId}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Title
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step Actions */}
            {steps.length > 0 && (
              <div className="p-3 border-t border-gray-100 bg-gray-50 space-y-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-center text-gray-600"
                  onClick={clearSteps}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Steps
                </Button>
                <Button 
                  size="sm" 
                  className="w-full justify-center bg-purple-600 hover:bg-purple-700"
                  onClick={openSaveModal}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Test
                </Button>
              </div>
            )}
          </>
        )}
        
        {/* Collapsed State */}
        {sidebarCollapsed && (
          <div className="flex-1 flex flex-col items-center py-4 gap-4">
            <div className="relative">
              <List className="h-5 w-5 text-gray-400" />
              {steps.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-purple-600 text-white text-xs rounded-full flex items-center justify-center">
                  {steps.length}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Header Bar */}
        <div className="h-14 px-6 flex items-center justify-between bg-gray-800/50 backdrop-blur border-b border-gray-700/50">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Monitor className="h-5 w-5 text-purple-400" />
              <span className="font-semibold text-white">Live Recorder</span>
            </div>
            
            {/* Divider */}
            <div className="w-px h-6 bg-gray-600" />
            
            {/* Status */}
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${statusInfo.color} ${statusInfo.pulse ? 'animate-pulse' : ''}`} />
              <span className="text-sm text-gray-400">{statusInfo.text}</span>
            </div>
            
            {/* Recording Badge & Timer */}
            {isRecording && (
              <>
                <Badge variant="destructive" className="animate-pulse">
                  <span className="mr-1">●</span> REC
                </Badge>
                <div className="flex items-center gap-1 text-gray-400">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-mono">{formatTime(recordingTime)}</span>
                </div>
              </>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {isRecording && vncStatus !== 'connected' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={reconnectVNC} 
                className="text-gray-300 border-gray-600 hover:bg-gray-700"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Reconnect
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose} 
              className="text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* Browser Area */}
        <div ref={vncContainerRef} className="flex-1 flex flex-col overflow-hidden">
          {!isRecording ? (
            // Start Screen
            <div className="flex-1 flex flex-col items-center justify-center p-8">
              <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl p-8 max-w-xl w-full shadow-2xl border border-gray-700/50">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg">
                    <Monitor className="h-10 w-10 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {isResuming ? 'Resume Recording' : 'Start Recording'}
                  </h2>
                  <p className="text-gray-400">
                    {isResuming 
                      ? `Continue recording from where you left off. The browser will open at the last URL.`
                      : 'Select an environment or enter a custom URL to launch a real browser session.'
                    }
                  </p>
                  {isResuming && initialTestName && (
                    <p className="text-purple-400 mt-2 text-sm">
                      Test: {initialTestName}
                    </p>
                  )}
                </div>
                
                <div className="space-y-5">
                  {/* Environment Selector */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                      <Server className="h-4 w-4" />
                      Select Environment
                    </label>
                    <Select
                      value={selectedEnvironmentId}
                      onValueChange={handleEnvironmentChange}
                      disabled={loadingEnvironments}
                    >
                      <SelectTrigger className="h-12 bg-gray-700/50 border-gray-600 text-white focus:border-purple-500 focus:ring-purple-500">
                        <SelectValue placeholder={loadingEnvironments ? "Loading environments..." : "Select an environment"} />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="custom" className="text-gray-200 focus:bg-gray-700 focus:text-white">
                          <div className="flex items-center gap-2">
                            <Link className="h-4 w-4 text-blue-400" />
                            <span>Custom URL</span>
                          </div>
                        </SelectItem>
                        {environments.length > 0 && (
                          <div className="border-t border-gray-700 my-1" />
                        )}
                        {environments.map((env) => (
                          <SelectItem 
                            key={env.id} 
                            value={env.id}
                            className="text-gray-200 focus:bg-gray-700 focus:text-white"
                          >
                            <div className="flex items-center gap-2">
                              <Server className="h-4 w-4 text-purple-400" />
                              <div className="flex flex-col items-start">
                                <span>{env.name}</span>
                                {env.base_url && (
                                  <span className="text-xs text-gray-400">{env.base_url}</span>
                                )}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* URL Input */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      {selectedEnvironmentId === 'custom' ? 'Enter URL' : 'Starting URL'}
                    </label>
                    <div className="relative">
                      <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                      <Input
                        type="url"
                        placeholder="https://example.com"
                        value={startingUrl}
                        onChange={(e) => setStartingUrl(e.target.value)}
                        className="pl-12 h-12 bg-gray-700/50 border-gray-600 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500"
                        onKeyDown={(e) => e.key === 'Enter' && startSession()}
                      />
                    </div>
                    {selectedEnvironmentId !== 'custom' && (
                      <p className="text-xs text-gray-500">
                        You can modify the URL or add a path to the base URL
                      </p>
                    )}
                  </div>
                  
                  <Button 
                    onClick={startSession}
                    disabled={isLoading || !startingUrl}
                    className="w-full h-14 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold text-lg shadow-lg disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Launching Browser...
                      </>
                    ) : (
                      <>
                        <Play className="h-5 w-5 mr-2" />
                        {isResuming ? 'Resume Recording' : 'Start Recording'}
                      </>
                    )}
                  </Button>
                  
                  {error && (
                    <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                      <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                      <span className="text-sm text-red-400">{error}</span>
                    </div>
                  )}
                </div>
                
                {/* Tips */}
                <div className="mt-8 pt-6 border-t border-gray-700/50">
                  <p className="text-xs text-gray-500 text-center">
                    💡 Tip: Click, type, and navigate naturally. Each action is captured as a replayable test step.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // VNC View
            <div className="flex-1 relative">
              {/* URL Bar */}
              <div className="absolute top-0 left-0 right-0 z-10 bg-gray-900/95 backdrop-blur px-4 py-2 flex items-center gap-3 border-b border-gray-700">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-lg flex-1">
                  <Globe className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <span className="text-sm text-gray-300 truncate">{currentUrl}</span>
                </div>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={stopSession}
                  disabled={isLoading}
                  className="flex-shrink-0 shadow-lg"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Square className="h-4 w-4 mr-2" />
                      Stop Recording
                    </>
                  )}
                </Button>
              </div>
              
              {/* VNC iframe */}
              <div className="absolute inset-0 pt-12">
                {vncIframeUrl ? (
                  <>
                    <iframe
                      ref={vncIframeRef}
                      src={vncIframeUrl}
                      className="w-full h-full border-0"
                      title="VNC Browser"
                      allow="clipboard-read; clipboard-write; keyboard-map"
                      tabIndex={0}
                      style={{ background: '#000' }}
                      onLoad={() => {
                        // Focus iframe on load for keyboard input
                        if (vncIframeRef.current) {
                          vncIframeRef.current.focus();
                        }
                      }}
                    />
                    
                    {/* Assertion Mode Overlay */}
                    {assertionMode && assertionMode !== 'title' && (
                      <div 
                        className="absolute inset-0 bg-emerald-500/10 cursor-crosshair flex items-center justify-center"
                        onClick={handleAssertionClick}
                      >
                        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-emerald-600 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3">
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-medium">
                            Click on an element to assert {assertionMode === 'text' ? 'text' : assertionMode === 'visible' ? 'visibility' : 'existence'}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-white hover:bg-emerald-700 ml-2"
                            onClick={(e) => { e.stopPropagation(); cancelAssertionMode(); }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Text Assertion Prompt Modal */}
                    {assertionPrompt && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {assertionPrompt.isTitle ? 'Assert Page Title' : 'Assert Text Content'}
                          </h3>
                          <p className="text-sm text-gray-500 mb-4">
                            {assertionPrompt.isTitle 
                              ? 'Enter the expected page title (or part of it):'
                              : `Element: ${assertionPrompt.elementInfo.selector}`
                            }
                          </p>
                          
                          <div className="space-y-4">
                            <div>
                              <label className="text-xs font-medium text-gray-600 mb-1 block">Expected Text</label>
                              <Input
                                id="assertion-text-input"
                                type="text"
                                defaultValue={assertionPrompt.defaultValue}
                                placeholder="Enter expected text..."
                                className="w-full"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    confirmTextAssertion(e.target.value);
                                  }
                                }}
                              />
                            </div>
                            
                            {assertionPrompt.elementInfo.text && (
                              <div className="p-3 bg-gray-50 rounded-lg">
                                <label className="text-xs font-medium text-gray-500 mb-1 block">Current Value</label>
                                <p className="text-sm text-gray-800 break-words">
                                  {assertionPrompt.elementInfo.text.substring(0, 200)}
                                  {assertionPrompt.elementInfo.text.length > 200 && '...'}
                                </p>
                              </div>
                            )}
                            
                            <div className="flex gap-3">
                              <Button 
                                variant="outline" 
                                className="flex-1"
                                onClick={cancelAssertionMode}
                              >
                                Cancel
                              </Button>
                              <Button 
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => {
                                  const input = document.getElementById('assertion-text-input');
                                  confirmTextAssertion(input?.value || assertionPrompt.defaultValue);
                                }}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Add Assertion
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-900">
                    {vncStatus === 'connecting' && (
                      <div className="text-center">
                        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-purple-400" />
                        <p className="text-gray-400 text-lg">Connecting to browser...</p>
                        <p className="text-gray-500 text-sm mt-1">This may take a few seconds</p>
                      </div>
                    )}
                    {vncStatus === 'error' && (
                      <div className="text-center">
                        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-400" />
                        <p className="text-red-400 text-lg mb-4">Connection Failed</p>
                        <Button 
                          variant="outline" 
                          onClick={reconnectVNC}
                          className="border-gray-600 text-gray-300 hover:bg-gray-800"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Retry Connection
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Save Test Name Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[2147483648]">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Save Test Case
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Enter a name for your test case ({steps.length} steps will be saved)
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Test Name</label>
                <Input
                  type="text"
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  placeholder="Enter test name..."
                  className="w-full"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && testName.trim()) {
                      saveTestWithName();
                    }
                  }}
                />
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowSaveModal(false);
                    setTestName('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={saveTestWithName}
                  disabled={!testName.trim()}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Test
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  
  // Use portal to render outside the main app container
  return ReactDOM.createPortal(modalContent, document.body);
};

export default VNCLiveRecorder;
